import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Check } from "lucide-react";

import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Turnstile } from "@/components/forms/Turnstile";
import {
  QQ_HEARD_OPTIONS,
  clearNorwood,
  readNorwood,
  rememberLeadId,
  rememberPhone,
  trackLeadEvent,
} from "@/components/forms/quick-quote-shared";
import { startQuickQuote } from "@/lib/forms/quick-quote.functions";
import { rememberW2lPreview } from "@/lib/forms/test-preview";
import { getTrackingParams } from "@/lib/utm-tracking";
import { DOUG_PORTRAIT, norwoodClasses } from "@/lib/site-data";
import { qqTrack, msSinceLoad } from "@/lib/qq-diagnostics";

const TITLE = "Get a Quick Quote - RESTORE Hair";
const DESCRIPTION =
  "Discover how affordable it is to get your hair back. Get a personalized quick quote on your No-Shave FUE hair restoration procedure from RESTORE Hair.";
const URL = "https://restorehair.com/quick-quote-part-1/";

export const Route = createFileRoute("/quick-quote-part-1")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, follow" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: QuickQuotePart1,
});

/** Boxed field with the label sitting inside the top of the control. */
function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="border border-border bg-background px-4 pb-2 pt-2.5">
      <label
        htmlFor={id}
        className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const controlCls =
  "w-full border-0 bg-transparent p-0 pt-1.5 pb-1 text-base text-foreground outline-none focus:ring-0";

function Consent({ id, children }: { id: string; children: ReactNode }) {
  return (
    <label className="flex gap-3" htmlFor={id}>
      <input id={id} name={id} type="checkbox" required className="mt-1 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </label>
  );
}

function QuickQuotePart1() {
  const navigate = useNavigate();
  const start = useServerFn(startQuickQuote);
  const [token, setToken] = useState<string | null>(null);
  const [norwood, setNorwood] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  // Set when Submit is tapped before Turnstile has a token; the form then
  // submits itself as soon as the token arrives.
  const pendingSubmitRef = useRef(false);
  const sentRef = useRef<Set<string>>(new Set());
  const once = (key: string, name: string, props: Record<string, string | number> = {}) => {
    if (sentRef.current.has(key)) return;
    sentRef.current.add(key);
    qqTrack(name, props);
  };

  function onTurnstileToken(t: string) {
    setToken(t);
    once("ready", "qq1_turnstile_ready", { ms: msSinceLoad() });
    if (pendingSubmitRef.current) {
      pendingSubmitRef.current = false;
      setError(null);
      formRef.current?.requestSubmit();
    }
  }

  // Norwood class chosen on another page (session storage). When none was
  // picked upstream, the picker is shown inline above the form instead of
  // redirecting — this page is the single funnel entry point.
  const [showPicker, setShowPicker] = useState(false);
  useEffect(() => {
    const stored = readNorwood();
    if (stored) {
      setNorwood(stored);
    } else {
      setShowPicker(true);
    }
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    // End-to-end tests set this flag; the server still rejects the missing
    // token unless the request also carries the x-rh-test secret header.
    const testRun = typeof window !== "undefined" && window.sessionStorage.getItem("rh_test") === "1";
    if (!token && !testRun) {
      // Before 30 Sep the button was silently disabled here; say why instead,
      // and submit automatically once verification finishes.
      pendingSubmitRef.current = true;
      setError("Verifying you're human. One moment, your quote will send automatically.");
      qqTrack("qq1_submit_blocked", { reason: "turnstile", ms: msSinceLoad() });
      return;
    }
    if (showPicker && !norwood) {
      setError("Please choose the image that most closely resembles your hair loss.");
      qqTrack("qq1_submit_blocked", { reason: "norwood", ms: msSinceLoad() });
      return;
    }
    const fd = new FormData(e.currentTarget);
    setSubmitting(true);
    setError(null);
    try {
      const firstName = String(fd.get("first_name") || "").trim();
      const lastName = String(fd.get("last_name") || "").trim();
      const emailValue = String(fd.get("email") || "").trim();
      const phoneValue = String(fd.get("phone") || "").trim();
      const zipValue = String(fd.get("zip") || "").trim();
      const res = await start({
        data: {
          turnstile_token: token ?? "test-mode",
          first_name: firstName,
          last_name: lastName,
          email: emailValue,
          phone: phoneValue,
          zip: zipValue,
          norwood: norwood || undefined,
          heard_about: String(fd.get("heard") || "").trim() || undefined,
          consent_contact: true,
          consent_terms: true,
          consent_sensitive: true,
          page_path: window.location.pathname,
          referrer: document.referrer || undefined,
          tracking: getTrackingParams(),
        },
      });
      rememberW2lPreview(res);
      await trackLeadEvent({
        em: emailValue,
        ph: phoneValue,
        fn: firstName,
        ln: lastName,
        zp: zipValue,
      });
      rememberLeadId(res.lead_id);
      rememberPhone(phoneValue);
      clearNorwood();
      await navigate({ to: "/quick-quote-page-2", search: { qq: res.lead_id } });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Something went wrong. Please try again.",
      );
      qqTrack("qq1_submit_error", {
        reason: (err instanceof Error ? err.message : "unknown").slice(0, 60),
      });
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10 lg:px-8 md:py-16">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left — brand column */}
          <div>
            <svg
              aria-hidden="true"
              viewBox="0 0 48 16"
              className="h-4 w-12 text-primary"
              fill="none"
            >
              <path
                d="M1 12C10 3 20 1 30 4c7 2 12 6 17 9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            <p className="mt-4 text-sm text-muted-foreground">RESTORE® Hair</p>
            <h1 className="mt-6 text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl">
              Get a Quick <span className="font-extrabold">Quote!</span>
            </h1>
            <p className="mt-6 text-base text-foreground">
              Discover how affordable it is to get your hair back.
            </p>

            <div className="mt-10 flex items-center gap-6">
              <img
                src={DOUG_PORTRAIT}
                alt="Doug M. before and after his RESTORE hair restoration procedure"
                width={104}
                height={104}
                loading="lazy"
                className="h-26 w-26 shrink-0 rounded-full object-cover shadow-md"
                style={{ height: "104px", width: "104px" }}
              />
              <div>
                <div className="flex gap-1 text-lg leading-none text-[#f5b301]" aria-label="5 out of 5 stars">
                  <span aria-hidden="true">★★★★★</span>
                </div>
                <p className="mt-3 max-w-xs text-base leading-relaxed text-foreground">
                  &ldquo;This was by far the best thing I&apos;ve ever done for myself.&rdquo; - Doug M
                </p>
              </div>
            </div>
          </div>

          {/* Right — form column */}
          <div>
            <div className="flex gap-3" aria-label="Step 1 of 2">
              <span className="h-1 flex-1 bg-foreground" />
              <span className="h-1 flex-1 bg-border" />
            </div>

            <h2 className="mt-8 text-2xl font-light text-foreground md:text-3xl">
              Tell Us About Yourself
            </h2>

            <form
              ref={formRef}
              onSubmit={onSubmit}
              onFocus={(e) => {
                const field = (e.target as { name?: string }).name || "unknown";
                once("first", "qq1_first_field", { field, ms: msSinceLoad() });
              }}
              onInvalid={(e) => {
                const field = (e.target as { name?: string }).name || "unknown";
                once(`invalid:${field}`, "qq1_validation_error", { field });
              }}
              className="mt-6 space-y-4"
            >
              {showPicker && (
                <fieldset>
                  <legend className="text-lg font-light text-foreground md:text-xl">
                    Which image most closely{" "}
                    <span className="font-extrabold">resembles your hair loss?</span>
                    <span className="text-destructive"> *</span>
                  </legend>
                  <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                    {norwoodClasses.map((c) => {
                      const selected = norwood === c.label;
                      return (
                        <button
                          type="button"
                          key={c.label}
                          aria-pressed={selected}
                          onClick={() => {
                            setNorwood(c.label);
                            setError(null);
                          }}
                          className={`group relative flex flex-col items-center border-2 bg-background p-3 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${
                            selected
                              ? "border-primary shadow-lg"
                              : "border-border hover:border-primary hover:bg-primary/5"
                          }`}
                        >
                          <span
                            className={`absolute -top-3 left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border-2 transition ${
                              selected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-muted bg-background text-muted-foreground"
                            }`}
                          >
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                          <img
                            src={c.img}
                            alt={`Norwood ${c.label} hair loss illustration`}
                            width={204}
                            height={130}
                            loading="lazy"
                            className="mt-3 h-20 w-auto origin-center object-contain transition-transform duration-200 ease-out [transform:scale(1)] group-hover:[transform:scale(1.12)]"
                          />
                          <span className="mt-2 text-sm font-bold text-foreground transition-colors group-hover:text-primary">
                            {c.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="first_name" label="First Name *">
                  <input className={controlCls} id="first_name" name="first_name" required maxLength={80} autoComplete="given-name" />
                </Field>
                <Field id="last_name" label="Last Name *">
                  <input className={controlCls} id="last_name" name="last_name" required maxLength={80} autoComplete="family-name" />
                </Field>
              </div>
              <Field id="phone" label="Phone *">
                <input
                  className={controlCls}
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  required
                  maxLength={32}
                  autoComplete="tel"
                  placeholder="(555) 555-5555"
                />
              </Field>
              <Field id="email" label="Email *">
                <input className={controlCls} id="email" name="email" type="email" required maxLength={255} autoComplete="email" />
              </Field>
              <Field id="zip" label="Zip Code *">
                <input className={controlCls} id="zip" name="zip" required maxLength={20} autoComplete="postal-code" />
              </Field>
              <Field id="heard" label="How did you hear about RESTORE?">
                <select className={controlCls} id="heard" name="heard" defaultValue="">
                  <option value="">Select one</option>
                  {QQ_HEARD_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </Field>

              {/* Norwood class carried over from the picker on another page */}
              <input type="hidden" name="norwood" value={norwood} />

              <div className="space-y-5 pt-2 text-xs leading-relaxed text-foreground">
                <Consent id="consent_contact">
                  By checking this box and clicking the Get a Quick Quote button below, I consent
                  to receive automated marketing, advertising, transactional, or informational
                  phone calls, text messages, or emails, potentially involving use of an
                  autodialer or an artificial or prerecorded voice, from or on behalf of Restore
                  Holdings LLC (“Restore”) at the phone number or email address I provided above.
                  I understand my consent is not a condition of purchase. Reply “STOP” to opt-out
                  of text messages.
                </Consent>
                <Consent id="consent_terms">
                  By checking this box and clicking the Get a Quick Quote button below, I also
                  agree to abide by Restore’s{" "}
                  <a className="text-primary underline" href="/terms-of-use/">Terms of Use</a> and{" "}
                  <a className="text-primary underline" href="/privacy-policy/">Privacy Policy</a>,
                  including submitting all disputes to binding individual arbitration.
                </Consent>
                <Consent id="consent_sensitive">
                  <span className="uppercase">
                    By checking this box and clicking the Get a Quick Quote button below, I consent
                    to Restore’s collection and processing of your “sensitive personal information”
                    for the purposes described in our{" "}
                    <a className="text-primary underline" href="/privacy-policy/">Privacy Policy</a>.
                  </span>
                </Consent>
                <p className="uppercase">
                  California Residents: See our{" "}
                  <a className="text-primary underline" href="/privacy-policy/#ccpa-notice">
                    CCPA notice at collection
                  </a>
                </p>
              </div>

              <div className="pt-2">
                <Turnstile
                  action="quick-quote"
                  onToken={onTurnstileToken}
                  onExpired={() => {
                    setToken(null);
                    qqTrack("qq1_turnstile_expired", { ms: msSinceLoad() });
                  }}
                  onError={() => {
                    setToken(null);
                    qqTrack("qq1_turnstile_error", { ms: msSinceLoad() });
                  }}
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Get A Quick Quote"}
              </button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
